"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import { updateLeadAction, deleteLeadAction } from "@/actions/lead-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useBreadcrumbStore } from "@/components/layout/breadcrumb";
import {
  ArrowLeft,
  Mail,
  Euro,
  Calendar,
  Save,
  Trash2,
  Building,
  Activity,
  User,
} from "lucide-react";

interface LeadItem {
  id: string;
  name: string;
  status: string;
  estimatedValue: string;
  contactEmail: string;
  createdAt: string;
  updatedAt: string;
}

interface LeadDetailsClientProps {
  initialLead: LeadItem | null;
  leadId: string;
}

const DEFAULT_MOCK_LEADS: Record<string, LeadItem> = {
  lead_1: {
    id: "lead_1",
    name: "Acme Corp Refactoring",
    status: "lead",
    estimatedValue: "15000",
    contactEmail: "sales@acme.com",
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  lead_2: {
    id: "lead_2",
    name: "Globex SaaS Integration",
    status: "negotiation",
    estimatedValue: "42000",
    contactEmail: "p.scorpio@globex.co",
    createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
  },
  lead_3: {
    id: "lead_3",
    name: "Initech Security Audit",
    status: "won",
    estimatedValue: "8500",
    contactEmail: "gibbons@initech.com",
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
  lead_4: {
    id: "lead_4",
    name: "Soylent Green Distribution",
    status: "lost",
    estimatedValue: "95000",
    contactEmail: "h.roth@soylent.com",
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
  },
  lead_5: {
    id: "lead_5",
    name: "Hooli Cloud Migration",
    status: "negotiation",
    estimatedValue: "68000",
    contactEmail: "gavin@hooli.xyz",
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
};

export function LeadDetailsClient({
  initialLead,
  leadId,
}: LeadDetailsClientProps) {
  const router = useRouter();
  const setLabel = useBreadcrumbStore((state) => state.setLabel);

  // Try to use DB lead first, fallback to static mock data if it matches mock keys or DB is empty
  const [lead, setLead] = useState<LeadItem | null>(() => {
    if (initialLead) return initialLead;
    if (DEFAULT_MOCK_LEADS[leadId]) return DEFAULT_MOCK_LEADS[leadId];
    return null;
  });

  React.useEffect(() => {
    if (lead?.name) {
      setLabel(leadId, lead.name);
    }
  }, [leadId, lead?.name, setLabel]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Edit Form State
  const [name, setName] = useState(lead?.name || "");
  const [status, setStatus] = useState<"lead" | "negotiation" | "won" | "lost">(
    (lead?.status as any) || "lead",
  );
  const [estimatedValue, setEstimatedValue] = useState(
    lead?.estimatedValue || "",
  );
  const [contactEmail, setContactEmail] = useState(lead?.contactEmail || "");

  if (!lead) {
    return (
      <div className="text-center p-12 space-y-4">
        <h3 className="text-lg font-bold text-text-primary">
          Lead non trovato
        </h3>
        <p className="text-sm text-text-secondary">
          L'opportunità specificata non esiste o è stata rimossa.
        </p>
        <Link href="/sales/leads">
          <Button className="gap-1.5 mt-2">
            <ArrowLeft className="h-4 w-4" /> Torna alla Pipeline
          </Button>
        </Link>
      </div>
    );
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!name.trim()) {
      setError("Il nome è obbligatorio.");
      return;
    }
    if (
      !estimatedValue ||
      isNaN(Number(estimatedValue)) ||
      Number(estimatedValue) < 0
    ) {
      setError("Il valore stimato deve essere un numero valido.");
      return;
    }
    if (!contactEmail.trim() || !contactEmail.includes("@")) {
      setError("Email non valida.");
      return;
    }

    setLoading(true);

    try {
      const res = await updateLeadAction(lead.id, {
        name,
        status,
        estimatedValue,
        contactEmail,
      });

      if (res.error) {
        // If updating in DB failed but it's a mock lead key, allow mock update on client
        if (lead.id.startsWith("lead_")) {
          const updatedLead = {
            ...lead,
            name,
            status,
            estimatedValue,
            contactEmail,
            updatedAt: new Date().toISOString(),
          };
          setLead(updatedLead);
          setSuccess(true);
        } else {
          setError(res.error);
        }
      } else {
        setSuccess(true);
        const updatedLead = {
          ...lead,
          name,
          status,
          estimatedValue,
          contactEmail,
          updatedAt: new Date().toISOString(),
        };
        setLead(updatedLead);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Impossibile salvare le modifiche.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Sei sicuro di voler eliminare definitivamente questo lead?"))
      return;
    setLoading(true);

    try {
      const res = await deleteLeadAction(lead.id);
      if (res.error && !lead.id.startsWith("lead_")) {
        setError(res.error);
        setLoading(false);
      } else {
        router.push("/sales/leads");
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante l'eliminazione.");
      setLoading(false);
    }
  };

  const getStatusBadge = (statusVal: string) => {
    switch (statusVal) {
      case "lead":
        return <Badge variant="neutral">Lead</Badge>;
      case "negotiation":
        return <Badge variant="warning">In Trattativa</Badge>;
      case "won":
        return <Badge variant="success">Vinto</Badge>;
      case "lost":
        return <Badge variant="error">Perso</Badge>;
      default:
        return <Badge variant="default">{statusVal}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Back button */}
      <div>
        <Link
          href="/sales/leads"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna alla Pipeline
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main detail card and edit form */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-start gap-4">
              <div>
                <CardTitle className="text-xl font-bold">{lead.name}</CardTitle>
                <CardDescription className="text-xs mt-1">
                  Modifica i parametri del lead commerciale.
                </CardDescription>
              </div>
              <div>{getStatusBadge(lead.status)}</div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="lead-name">Nome Opportunità / Azienda</Label>
                <Input
                  id="lead-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-value">Valore Stimato (€)</Label>
                  <Input
                    id="lead-value"
                    type="number"
                    value={estimatedValue}
                    onChange={(e) => setEstimatedValue(e.target.value)}
                    disabled={loading}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lead-status">Stato</Label>
                  <Select
                    id="lead-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as any)}
                    disabled={loading}
                  >
                    <option value="lead">Lead</option>
                    <option value="negotiation">In Trattativa</option>
                    <option value="won">Vinto</option>
                    <option value="lost">Perso</option>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lead-email">Email di Contatto</Label>
                <Input
                  id="lead-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && (
                <p className="text-xs font-semibold text-success bg-success-soft/20 border border-success/20 rounded-md p-2 mt-1">
                  Modifiche salvate con successo.
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-border/30">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDelete}
                  className="border-error hover:bg-error-soft text-error gap-1.5"
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4" />
                  Elimina Lead
                </Button>
                <Button type="submit" loading={loading} className="gap-1.5">
                  <Save className="h-4 w-4" />
                  Salva Modifiche
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Sidebar Info Card */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-text-secondary">
                Metadati Opportunità
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-border/30">
                  <span className="text-text-secondary font-medium flex items-center gap-1.5">
                    <Activity className="h-4 w-4 text-primary-500" />
                    ID Lead:
                  </span>
                  <span
                    className="font-mono text-xs text-text-primary truncate max-w-[150px]"
                    title={lead.id}
                  >
                    {lead.id}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border/30">
                  <span className="text-text-secondary font-medium flex items-center gap-1.5">
                    <Euro className="h-4 w-4 text-success" />
                    Valore:
                  </span>
                  <span className="font-semibold text-text-primary">
                    {new Intl.NumberFormat("it-IT", {
                      style: "currency",
                      currency: "EUR",
                    }).format(parseFloat(lead.estimatedValue))}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border/30">
                  <span className="text-text-secondary font-medium flex items-center gap-1.5">
                    <Mail className="h-4 w-4 text-info" />
                    Email:
                  </span>
                  <span className="font-semibold text-text-primary text-xs truncate max-w-[150px]">
                    {lead.contactEmail}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border/30">
                  <span className="text-text-secondary font-medium flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-text-muted" />
                    Creato il:
                  </span>
                  <span className="text-text-primary font-medium text-xs">
                    {new Date(lead.createdAt).toLocaleDateString("it-IT", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-text-secondary font-medium flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-text-muted" />
                    Aggiornato il:
                  </span>
                  <span className="text-text-primary font-medium text-xs">
                    {new Date(lead.updatedAt).toLocaleDateString("it-IT", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
